import {
  closeChatRoom,
  createChatRoom,
  downloadMessageZipFile,
  getSignedUrlForGet,
  reopenChatRoom,
  sendMessage,
  toggleMessagePin,
  updateChatRoomReadStatus,
  updateMessage,
  useChatRoom,
  useInvalidateChatRooms,
  type ChatRoomChannel,
} from '@commenda/api';
import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import type { DropdownMenuItemProps } from '@commenda/kakashi';
import {
  Badge,
  Dropdown,
  IconButton,
  Switcher,
  SwitcherItem,
  SwitcherList,
  Tabs,
  TabsContent,
  Tooltip,
} from '@commenda/kakashi';
import { Icons } from '@commenda/kakashi/src/components/Icons';
import mixpanel, {
  MIXPANEL_EVENTS,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import { IChatRoomMessage } from '@commenda/types';
import { FileBasic } from '@commenda/types/src/file/types';
import { ChatRoomModel, cn, type ChatRoomParentTab } from '@commenda/utils';
import {
  getSignedUrlByFileId,
  uploadFile,
} from '@commenda/utils/files/uploadFile';
import { ChatRoomType } from '@prisma/client';
import { useEffect, useRef, useState } from 'react';
import { useNotification } from '../../hooks/useNotification';
import ChatHeader from '../components/ChatHeader';
import ChatRoomTabList from '../components/ChatRoomTabList';
import ChatView from '../components/MessagesView/ChatView';
import DocumentsView from '../components/MessagesView/DocumentsView';
import PinnedChatView from '../components/MessagesView/PinnedChatView';
import { useChatRoomTabs } from '../hooks/useChatRoomTabs';
import { useThreadAgentActions } from '../hooks/useThreadAgentActions';
import { ThreadSubscribers } from '../ThreadSubscribers';
import {
  BookkeepingChatRoomInfo,
  ChatRoomProps,
  ComplianceItemChatRoomInfo,
  GeneralChatRoomInfo,
  MarketplaceServiceChatRoomInfo,
  ServiceChatRoomInfo,
} from './types';

const ChatRoom = (props: ChatRoomProps) => {
  const isAgent = props.variant !== 'customer-only';
  const { initialParentTab: initialParentTabProp, supportTooltipOverride } =
    props;

  // ── Agent-only state: chatRoomId is managed internally ──────────────────
  const [agentChatRoomId, setAgentChatRoomId] = useState<string | null>(
    isAgent ? (props.chatRoomInfo?.chatRoomId ?? null) : null
  );
  const incomingAgentChatRoomId = isAgent
    ? (props.chatRoomInfo?.chatRoomId ?? null)
    : null;

  useEffect(() => {
    if (!isAgent) return;

    if (agentChatRoomId !== incomingAgentChatRoomId) {
      setAgentChatRoomId(incomingAgentChatRoomId);
    }
  }, [isAgent, incomingAgentChatRoomId, agentChatRoomId]);

  // The effective chatRoomId depends on variant
  const chatRoomId = isAgent
    ? agentChatRoomId
    : (props as { chatRoomId: string | null }).chatRoomId;

  // ── Channel selection ─────────────────────────────────────────────────────
  const initialParentTab: ChatRoomParentTab =
    initialParentTabProp ?? 'CUSTOMER';
  const [activeParentTab, setActiveParentTab] =
    useState<ChatRoomParentTab>(initialParentTab);

  const hideInternalNotes = isAgent
    ? ((props as { hideInternalNotes?: boolean }).hideInternalNotes ?? false)
    : true;
  const supportTooltipCopy =
    supportTooltipOverride ??
    (isAgent && !hideInternalNotes
      ? 'Messages with your client'
      : 'Messages with your service team');

  // Works for both agent and customer-only variants.
  const includeCustomerInternalNotes =
    (props as { includeCustomerInternalNotes?: boolean })
      .includeCustomerInternalNotes ?? false;

  // The channel we pass to mark-as-read.
  const channelForRead: ChatRoomChannel = ChatRoomModel.resolveChannelForRead({
    isAgent,
    hideInternalNotes,
    includeCustomerInternalNotes,
    activeParentTab,
  });

  // ── Data fetching ─────────────────────────────────────────────────────────
  // Preload all relevant channels in parallel on mount so tab switches are
  // instant (no key change → no loading flash).
  const {
    data: customerChannelData,
    watchForAgentReply: watchCustomerChannelForAgentReply,
  } = useChatRoom({
    chatRoomId,
    channel: 'CUSTOMER',
  });
  const {
    data: internalChannelData,
    watchForAgentReply: watchInternalChannelForAgentReply,
  } = useChatRoom({
    // Skip for customer-only or when agent internal notes are hidden.
    chatRoomId: isAgent && !hideInternalNotes ? chatRoomId : null,
    channel: 'AGENT_INTERNAL',
  });
  const {
    data: customerInternalChannelData,
    watchForAgentReply: watchCustomerInternalChannelForAgentReply,
  } = useChatRoom({
    // Fetch for both variants when opt-in — covers CLIENT users in agent layout too.
    chatRoomId: includeCustomerInternalNotes ? chatRoomId : null,
    channel: 'CUSTOMER_INTERNAL',
  });

  // The active response is whichever channel tab is selected.
  const chatRoomResponse =
    activeParentTab === 'INTERNAL'
      ? internalChannelData
      : activeParentTab === 'CUSTOMER_INTERNAL'
        ? customerInternalChannelData
        : customerChannelData;
  const watchForAgentReply =
    activeParentTab === 'INTERNAL'
      ? watchInternalChannelForAgentReply
      : activeParentTab === 'CUSTOMER_INTERNAL'
        ? watchCustomerInternalChannelForAgentReply
        : watchCustomerChannelForAgentReply;

  // Metadata (type, unread counts) comes from the customer channel response
  // since it always exists and channels[] is populated from the full room state.
  const metaResponse = customerChannelData;

  const chatRoom = chatRoomResponse?.chatRoom;
  const chatRoomType = isAgent
    ? (metaResponse?.chatRoom?.type ??
      (
        props as ChatRoomProps & {
          chatRoomInfo?: { chatRoomType: ChatRoomType };
        }
      ).chatRoomInfo?.chatRoomType)
    : chatRoom?.type;

  const forceShowTabs = (props as { forceShowTabs?: boolean }).forceShowTabs;
  const initialMessage = (props as { initialMessage?: string }).initialMessage;
  const isGeneralThread = chatRoomType === 'GENERAL_THREAD' && !forceShowTabs;

  // ── Tab state ─────────────────────────────────────────────────────────────
  const [currentTab, setCurrentTab] = useState<string>('chat');
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(
    null
  );
  const { isSearchActive, searchQuery, setSearchQuery } = useChatRoomTabs();

  const { setNotification } = useNotification();
  const invalidateChatRooms = useInvalidateChatRooms();
  const agentReplyActions = useThreadAgentActions({
    chatRoomId,
    channel: channelForRead,
    isAgentMuted: chatRoom?.agentState?.isMuted ?? false,
  });

  const navigateToMessage = (messageId: string) => {
    setSelectedMessageId(messageId);
    setCurrentTab('chat');
  };

  // ── Mark as read ──────────────────────────────────────────────────────────
  const open = isAgent ? true : (props as { open: boolean }).open;
  const hasPendingAgentReply =
    (chatRoom?.agentState?.pendingTriggerMessageIds?.length ?? 0) > 0;

  useEffect(() => {
    if (!open || !chatRoomId) return;

    let cancelled = false;
    const run = async () => {
      try {
        await updateChatRoomReadStatus(chatRoomId, undefined, {
          channel: channelForRead,
        });
        if (cancelled) return;
        // Invalidate list (React Query) + all SWR keys for this room, then run any caller side-effects.
        invalidateChatRooms(chatRoomId);
        await props.afterChange?.();
      } catch (err) {
        if (!cancelled) {
          console.error('[ChatRoom] Mark as read failed:', err);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [open, chatRoomId, channelForRead, isAgent, hasPendingAgentReply]);

  // ── Messages ──────────────────────────────────────────────────────────────
  // Buffers hold the last resolved messages per channel. They are updated
  // synchronously during render when new data arrives, so switching tabs
  // immediately shows the cached channel data instead of flashing empty.
  // This also prevents the "clear then reload" flash that occurs when SWR
  // revalidates after a mark-as-read or send action.
  const customerMsgsBuffer = useRef<IChatRoomMessage[]>([]);
  const internalMsgsBuffer = useRef<IChatRoomMessage[]>([]);
  const customerInternalMsgsBuffer = useRef<IChatRoomMessage[]>([]);

  const freshCustomerMsgs = customerChannelData?.chatRoom?.chatMessages;
  const freshInternalMsgs = internalChannelData?.chatRoom?.chatMessages;
  const freshCustomerInternalMsgs =
    customerInternalChannelData?.chatRoom?.chatMessages;
  if (freshCustomerMsgs != null) customerMsgsBuffer.current = freshCustomerMsgs;
  if (freshInternalMsgs != null) internalMsgsBuffer.current = freshInternalMsgs;
  if (freshCustomerInternalMsgs != null)
    customerInternalMsgsBuffer.current = freshCustomerInternalMsgs;

  const activeMessages =
    activeParentTab === 'INTERNAL'
      ? internalMsgsBuffer.current
      : activeParentTab === 'CUSTOMER_INTERNAL'
        ? customerInternalMsgsBuffer.current
        : customerMsgsBuffer.current;

  // ── Unread counts ─────────────────────────────────────────────────────────
  // Always derive from metaResponse (customer channel), which carries channels[].
  const metaChatRoom = metaResponse?.chatRoom;
  const unreadCustomerMessagesCount = metaChatRoom
    ? ChatRoomModel.getChannelUnread(metaChatRoom, 'CUSTOMER')
    : 0;
  const unreadInternalMessagesCount = metaChatRoom
    ? ChatRoomModel.getChannelUnread(metaChatRoom, 'AGENT_INTERNAL')
    : 0;
  const unreadCustomerInternalMessagesCount = metaChatRoom
    ? ChatRoomModel.getChannelUnread(metaChatRoom, 'CUSTOMER_INTERNAL')
    : 0;

  const isChatClosed =
    metaChatRoom?.isClosed ?? props.isChatClosedOverride ?? false;

  const allowReopenChatRoom = props.allowReopenChatRoom ?? false;
  const allowCloseChatRoom = props.allowCloseChatRoom ?? false;

  const onReopenChatRoom = async () => {
    if (!chatRoomId) return;
    try {
      await reopenChatRoom(chatRoomId, isAgent ? undefined : null);
      invalidateChatRooms(chatRoomId);
      await props.afterChange?.();
    } catch {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to reopen chat',
        message: 'Failed to reopen the chat. Please refresh and try again.',
      });
    }
  };

  const onCloseChatRoom = async () => {
    if (!chatRoomId) return;
    try {
      await closeChatRoom(chatRoomId, isAgent ? undefined : null);
      invalidateChatRooms(chatRoomId);
      await props.afterChange?.();
    } catch {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to close chatroom',
        message: 'Failed to close the chatroom. Please refresh and try again.',
      });
    }
  };

  const getChatroomDropdownOptions = (): DropdownMenuItemProps[] => {
    if (!chatRoomId || isGeneralThread) return [];
    if (isChatClosed) {
      return allowReopenChatRoom
        ? [
            {
              label: 'Reopen chat',
              onClick: onReopenChatRoom,
              icon: Icons.RefreshCcw01,
            },
          ]
        : [];
    }
    return allowCloseChatRoom
      ? [
          {
            label: 'Close chatroom',
            onClick: onCloseChatRoom,
            icon: Icons.MessageXCircle,
          },
        ]
      : [];
  };

  // ── File helpers ──────────────────────────────────────────────────────────
  const onFileDownload = async (fileId: number) => {
    if (isAgent) {
      const signedUrl = await getSignedUrlByFileId(fileId, undefined);
      return { signedUrl };
    }
    return getSignedUrlForGet(fileId);
  };

  const onFileUpload = async (file: File) => {
    const resolvedCompanyId =
      props.companyId ??
      metaResponse?.chatRoom?.companyId ??
      chatRoom?.companyId;
    if (!resolvedCompanyId) {
      return { errorMessage: 'This thread is not linked to a company yet.' };
    }
    const startedAt = Date.now();
    const res = await uploadFile(
      file,
      resolvedCompanyId,
      isAgent ? undefined : null
    );
    if (res.outcome === 'failed') {
      if (!isAgent) {
        mixpanel.track(MIXPANEL_EVENTS.DOCUMENT_UPLOAD_FAILED_ON_THREADS, {
          surface_area: MIXPANEL_SURFACE_AREA.THREADS,
          file_name: file.name,
          file_size_bytes: file.size,
          file_type: file.type,
          failure_stage: res.stage,
          status_code: res.status,
          duration_ms: Date.now() - startedAt,
        });
      }
      return { errorMessage: res.reason };
    }
    if (!isAgent) {
      mixpanel.track(MIXPANEL_EVENTS.DOCUMENT_UPLOADED_ON_THREADS, {
        surface_area: MIXPANEL_SURFACE_AREA.THREADS,
        file_name: file.name,
      });
    }
    return { fileUrl: res.fileUrl };
  };

  // ── Message actions ───────────────────────────────────────────────────────
  const onToggleMessagePin = async (messageId: string) => {
    await toggleMessagePin(chatRoomId, messageId);
    if (!isAgent) {
      mixpanel.track(MIXPANEL_EVENTS.PINNED_MESSAGE, {
        surface_area: MIXPANEL_SURFACE_AREA.THREADS,
        message_id: messageId,
      });
    }
    invalidateChatRooms(chatRoomId);
    await props.afterChange?.();
  };

  const onDownloadMessageZipFile = async (messageId: string) => {
    const res = await downloadMessageZipFile(
      chatRoomId,
      messageId,
      isAgent ? undefined : null
    );
    const result = await res.json();
    if (!isAgent) {
      mixpanel.track(MIXPANEL_EVENTS.DOWNLOADED_DOCUMENT_FROM_THREADS, {
        surface_area: MIXPANEL_SURFACE_AREA.THREADS,
        download_type: 'zip',
        message_id: messageId,
      });
    }
    return { signedUrl: result.signedUrl };
  };

  const onUpdate = async (args: { files: FileBasic[]; messageId: string }) => {
    const res = await updateMessage(
      chatRoomId,
      args.messageId,
      { files: args.files },
      isAgent ? undefined : null
    );

    if (!res) {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to update message',
        message: 'Failed to update your message. Please refresh and try again.',
      });
      return false;
    }

    if (isAgent) {
      setNotification({
        show: true,
        valid: true,
        title: 'Updated your files',
        message: 'Your files have been successfully uploaded.',
      });
    }

    invalidateChatRooms(chatRoomId);
    await props.afterChange?.();
    return true;
  };

  // ── Send ──────────────────────────────────────────────────────────────────
  const onSend = async (args: {
    message: string;
    messageType: string;
    files: FileBasic[];
    replyToMessageId?: string;
    mentionedUserIds?: string[];
    mentionsAgent?: boolean;
  }) => {
    let newChatRoomId = chatRoomId;

    if (isAgent) {
      // Agent: create room from chatRoomInfo on first send
      if (!agentChatRoomId) {
        let res: Awaited<ReturnType<typeof createChatRoom>>;
        const chRoomInfo = props.chatRoomInfo;

        if (chatRoomType === ChatRoomType.COMPLIANCE_THREAD) {
          const info = chRoomInfo as ComplianceItemChatRoomInfo;
          res = await createChatRoom({
            createChatRoomProps: {
              chatRoomType: ChatRoomType.COMPLIANCE_THREAD,
              complianceId: info.filingId,
              serviceRequestId: info.serviceRequestId,
            },
            token: undefined,
          });
        } else if (chatRoomType === ChatRoomType.MARKETPLACE_SERVICE_THREAD) {
          const info = chRoomInfo as MarketplaceServiceChatRoomInfo;
          res = await createChatRoom({
            createChatRoomProps: {
              chatRoomType: ChatRoomType.MARKETPLACE_SERVICE_THREAD,
              marketplaceServiceId: info.marketplaceServiceId,
              companyId: props.companyId,
            },
            token: undefined,
          });
        } else if (chatRoomType === ChatRoomType.BOOKKEEPING_THREAD) {
          const info = chRoomInfo as BookkeepingChatRoomInfo;
          res = await createChatRoom({
            createChatRoomProps: {
              chatRoomType: ChatRoomType.BOOKKEEPING_THREAD,
              corporationId: info.corporationId,
            },
            token: undefined,
          });
        } else if (chatRoomType === ChatRoomType.SERVICE_REQUEST_THREAD) {
          const info = chRoomInfo as ServiceChatRoomInfo;
          res = await createChatRoom({
            createChatRoomProps: {
              chatRoomType: ChatRoomType.SERVICE_REQUEST_THREAD,
              serviceRequestId: info.serviceRequestId,
              complianceId: info.complianceId,
            },
            token: undefined,
          });
        } else if (chatRoomType === 'GENERAL_THREAD') {
          const info = chRoomInfo as GeneralChatRoomInfo;
          res = await createChatRoom({
            createChatRoomProps: {
              chatRoomType: 'GENERAL_THREAD',
              companyId: info.companyId,
            },
            token: undefined,
          });
        }

        if (!res.ok) {
          setNotification({
            show: true,
            valid: false,
            title: 'Failed to send message',
            message:
              'Failed to send your message. Please refresh and try again.',
          });
          invalidateChatRooms(chatRoomId);
          await props.afterChange?.();
          return false;
        }
        const result = await res.json();
        newChatRoomId = result?.chatRoom?.id;
        setAgentChatRoomId(newChatRoomId);
        invalidateChatRooms(newChatRoomId);
      }
    } else {
      // Customer-only: create room via callback if needed
      const p = props as {
        chatRoomId: string | null;
        onCreateOrThrowChatRoom?: () => Promise<string>;
      };
      if (!p.chatRoomId) {
        if (!p.onCreateOrThrowChatRoom) {
          setNotification({
            show: true,
            valid: false,
            title: 'Failed to send message',
            message: 'Chat room does not exist',
          });
          return false;
        }
        try {
          newChatRoomId = await p.onCreateOrThrowChatRoom();
        } catch {
          setNotification({
            show: true,
            valid: false,
            title: 'Failed to send message',
            message: 'Failed to create chat room',
          });
          return false;
        }
        if (!newChatRoomId) {
          setNotification({
            show: true,
            valid: false,
            title: 'Failed to send message',
            message: 'Failed to successfully create chat room',
          });
          return false;
        }
      }
    }

    const { channel: sendChannel, isInternalNoteMessage } =
      ChatRoomModel.resolveSendChannel({
        isAgent,
        activeParentTab,
        includeCustomerInternalNotes,
      });
    const res = await sendMessage(
      {
        chatRoomId: newChatRoomId,
        message: args.message,
        messageType: args.messageType as any,
        sentAt: new Date(),
        files: args.files,
        replyToMessageId: args?.replyToMessageId || null,
        isInternalNoteMessage,
        channel: sendChannel,
        mentionedUserIds: args.mentionedUserIds,
        mentionsAgent: args.mentionsAgent,
      },
      isAgent ? undefined : null
    );

    if (res && !args.mentionsAgent && chatRoom?.agentState?.isListening) {
      watchForAgentReply();
    }

    if (res && args.mentionsAgent) {
      mixpanel.track(MIXPANEL_EVENTS.THREAD_AGENT_MENTIONED, {
        surface_area: MIXPANEL_SURFACE_AREA.THREADS,
        chat_room_id: newChatRoomId,
        channel: channelForRead,
      });
    }

    if (isAgent && activeParentTab === 'INTERNAL') {
      mixpanel.track(MIXPANEL_EVENTS.UPDATES_IN_INTERNAL_CHAT, {
        surface_area: MIXPANEL_SURFACE_AREA.AGENT_APP,
      });
    } else if (!isAgent) {
      mixpanel.track(MIXPANEL_EVENTS.MESSAGE_SENT, {
        surface_area: MIXPANEL_SURFACE_AREA.THREADS,
        message_type: args.messageType,
      });
      if (args?.replyToMessageId) {
        mixpanel.track(MIXPANEL_EVENTS.REPLY_TO_MESSAGE_USED, {
          surface_area: MIXPANEL_SURFACE_AREA.THREADS,
          reply_to_message_id: args.replyToMessageId,
        });
      }
    }

    invalidateChatRooms(newChatRoomId ?? chatRoomId);
    await props.afterChange?.();

    if (!res) {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to send message',
        message: 'Failed to send your message. Please refresh and try again.',
      });
      return false;
    }
    return true;
  };

  // ── Customer-only: visibility gate ────────────────────────────────────────
  if (!isAgent && !open) return null;

  // ── Customer-only layout (popover) ────────────────────────────────────────
  if (!isAgent) {
    const p = props as {
      onBackToChatList?: () => void;
      goToConnectedFilling?: () => void;
    };
    return (
      <div
        style={{
          boxShadow: '0px 1px 2px 0px #14120F0F, 0px 1px 3px 0px #14120F1A',
        }}
        className="absolute top-[8px] right-0 w-[500px] z-50 flex flex-col border border-border-primary rounded-md overflow-clip"
      >
        <ChatHeader
          goToConnectedFilling={p.goToConnectedFilling}
          onBack={p.onBackToChatList}
          title={isGeneralThread ? 'Account Manager' : chatRoom?.displayName}
        />
        {!isGeneralThread && includeCustomerInternalNotes && (
          <div className="pt-xl pb-xs px-xl">
            <Switcher
              value={activeParentTab}
              onValueChange={(value) =>
                setActiveParentTab(value as 'CUSTOMER' | 'CUSTOMER_INTERNAL')
              }
              defaultValue={initialParentTab}
            >
              <SwitcherList aria-label="Conversation audience">
                <SwitcherItem value="CUSTOMER">
                  <div className="flex items-center gap-lg group">
                    <div className="flex items-center gap-sm">
                      <Icons.Headphones01 className="size-4 text-inherit" />
                      <span>Support</span>
                      <Tooltip content={supportTooltipCopy}>
                        <Icons.InfoCircle
                          className="size-3 text-text-disabled group-hover:text-text-secondary"
                          aria-label="Support messages info"
                        />
                      </Tooltip>
                    </div>
                    {!!unreadCustomerMessagesCount && (
                      <Badge size="sm">{unreadCustomerMessagesCount}</Badge>
                    )}
                  </div>
                </SwitcherItem>
                <SwitcherItem value="CUSTOMER_INTERNAL">
                  <div className="flex items-center gap-lg">
                    <div className="flex items-center gap-sm">
                      <Icons.Users01 className="size-4 text-inherit" />
                      <span>Team</span>
                      <Tooltip content="Internal messages with your organization">
                        <Icons.InfoCircle
                          className="size-3 text-text-disabled group-hover:text-text-secondary"
                          aria-label="Team messages info"
                        />
                      </Tooltip>
                    </div>
                    {!!unreadCustomerInternalMessagesCount && (
                      <Badge size="sm">
                        {unreadCustomerInternalMessagesCount}
                      </Badge>
                    )}
                  </div>
                </SwitcherItem>
              </SwitcherList>
            </Switcher>
          </div>
        )}
        {isGeneralThread ? (
          <div className="flex flex-col flex-grow h-[70vh] rounded-b-md bg-white">
            <ChatView
              chatRoom={chatRoom}
              onSend={onSend}
              onFileDownload={onFileDownload}
              onFileUpload={onFileUpload}
              messages={activeMessages}
              onToggleMessagePin={onToggleMessagePin}
              isChatClosed={false}
              selectedMessageId={selectedMessageId}
              clearSelectedMessageId={() => setSelectedMessageId(null)}
              userId={props.userId}
              shouldShowAdditionalControls={false}
              searchQuery={searchQuery}
              activeParentTab={activeParentTab}
              agentReplyActions={agentReplyActions}
              isGeneralThread
              initialContent={initialMessage}
            />
          </div>
        ) : (
          <Tabs
            value={currentTab}
            onValueChange={setCurrentTab}
            className="flex flex-col flex-grow"
          >
            <ChatRoomTabList
              isSearchActive={isSearchActive}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              activeTab={currentTab}
              onTabChange={setCurrentTab}
            />
            <TabsContent
              value="chat"
              className="h-[70vh] rounded-b-md bg-white"
            >
              <ChatView
                chatRoom={chatRoom}
                onSend={onSend}
                onFileDownload={onFileDownload}
                onFileUpload={onFileUpload}
                messages={activeMessages}
                onToggleMessagePin={onToggleMessagePin}
                isChatClosed={isChatClosed}
                selectedMessageId={selectedMessageId}
                clearSelectedMessageId={() => setSelectedMessageId(null)}
                userId={props.userId}
                shouldShowAdditionalControls={false}
                searchQuery={searchQuery}
                activeParentTab={activeParentTab}
                agentReplyActions={agentReplyActions}
              />
            </TabsContent>
            <TabsContent
              value="documents"
              className="h-[70vh] rounded-b-md bg-white"
            >
              <DocumentsView
                messages={activeMessages}
                onFileDownload={onFileDownload}
              />
            </TabsContent>
            <TabsContent
              value="pinned"
              className="h-[70vh] rounded-b-md bg-white"
            >
              <PinnedChatView
                onFileDownload={onFileDownload}
                messages={activeMessages}
                onMessageClick={navigateToMessage}
                onToggleMessagePin={onToggleMessagePin}
                userId={props.userId}
              />
            </TabsContent>
          </Tabs>
        )}
      </div>
    );
  }

  // ── Agent layout (full-height panel) ─────────────────────────────────────
  const {
    onClose,
    noBorder = false,
    fullWidth = false,
    hideFeatures = false,
    hideHeader = false,
  } = props as {
    onClose?: () => void;
    noBorder?: boolean;
    fullWidth?: boolean;
    hideFeatures?: boolean;
    hideHeader?: boolean;
  };

  // Who may edit thread subscribers: explicit prop wins (admin app passes 'ADMIN'),
  // otherwise infer from the variant — agent layout edits the agent side, customer
  // layout edits the company side.
  const subscriberViewerRole =
    props.subscriberViewerRole ?? (isAgent ? 'AGENT' : 'CUSTOMER');

  const chatroomDropdownOptions = getChatroomDropdownOptions();

  return (
    <div
      className={cn(
        'h-full min-h-0 flex-1 flex flex-col w-full',
        !fullWidth && 'max-w-[500px]',
        !hideHeader && !noBorder && 'border-l border-border-primary'
      )}
      id={`chat-room-new-${chatRoomId}`}
    >
      {!hideHeader && (
        <div className="py-lg px-xl bg-bg-primary border-b border-border-primary flex justify-between items-center">
          <h3 className="font-mulish text-base font-bold leading-6 text-text-brand">
            Inbox
          </h3>
          <div className="flex items-center gap-sm">
            {chatRoomId &&
              isFeatureLaunched(FeatureLaunch.THREAD_SUBSCRIPTIONS) && (
                <ThreadSubscribers
                  chatRoomId={chatRoomId}
                  viewerRole={subscriberViewerRole}
                  currentUserId={props.userId}
                />
              )}
            {chatroomDropdownOptions.length > 0 && (
              <Dropdown
                align="end"
                sideOffset={6}
                menu={{ groups: [{ items: chatroomDropdownOptions }] }}
              >
                <IconButton
                  aria-label="Chat options"
                  icon={<Icons.DotsVertical />}
                  size="sm"
                />
              </Dropdown>
            )}
            {onClose && (
              <IconButton
                aria-label="Close"
                icon={<Icons.XClose />}
                onClick={onClose}
                size="sm"
              />
            )}
          </div>
        </div>
      )}
      {!isGeneralThread &&
        (!hideInternalNotes || includeCustomerInternalNotes) && (
          <div className="pt-xl pb-xs px-xl">
            <Switcher
              value={activeParentTab}
              onValueChange={(value) =>
                setActiveParentTab(
                  value as 'CUSTOMER' | 'INTERNAL' | 'CUSTOMER_INTERNAL'
                )
              }
              defaultValue={initialParentTab}
            >
              <SwitcherList aria-label="Conversation audience">
                <SwitcherItem value="CUSTOMER">
                  <div className="flex items-center gap-lg">
                    <div className="flex items-center gap-sm">
                      <Icons.Headphones01 className="size-4 text-inherit" />
                      <span>Support</span>
                      <Tooltip content={supportTooltipCopy}>
                        <Icons.InfoCircle
                          className="size-3 text-text-disabled group-hover:text-text-secondary"
                          aria-label="Support messages info"
                        />
                      </Tooltip>
                    </div>
                    {!!unreadCustomerMessagesCount && (
                      <Badge size="sm">{unreadCustomerMessagesCount}</Badge>
                    )}
                  </div>
                </SwitcherItem>
                {includeCustomerInternalNotes ? (
                  <SwitcherItem value="CUSTOMER_INTERNAL">
                    <div className="flex items-center gap-lg">
                      <div className="flex items-center gap-sm">
                        <Icons.Users01 className="size-4 text-inherit" />
                        <span>Team</span>
                        <Tooltip content="Internal messages with your organization">
                          <Icons.InfoCircle
                            className="size-3 text-text-disabled group-hover:text-text-secondary"
                            aria-label="Team messages info"
                          />
                        </Tooltip>
                      </div>
                      {!!unreadCustomerInternalMessagesCount && (
                        <Badge size="sm">
                          {unreadCustomerInternalMessagesCount}
                        </Badge>
                      )}
                    </div>
                  </SwitcherItem>
                ) : (
                  <SwitcherItem value="INTERNAL">
                    <div className="flex items-center gap-lg">
                      <div className="flex items-center gap-sm">
                        <Icons.Users01 className="size-4 text-inherit" />
                        <span>Team</span>
                        <Tooltip content="Internal messages with your organization">
                          <Icons.InfoCircle
                            className="size-3 text-text-disabled group-hover:text-text-secondary"
                            aria-label="Team messages info"
                          />
                        </Tooltip>
                      </div>
                      {!!unreadInternalMessagesCount && (
                        <Badge size="sm">{unreadInternalMessagesCount}</Badge>
                      )}
                    </div>
                  </SwitcherItem>
                )}
              </SwitcherList>
            </Switcher>
          </div>
        )}
      <div className="flex flex-col justify-between flex-1 min-h-0 overflow-hidden">
        {isGeneralThread ? (
          <div className="flex flex-col h-full min-h-0">
            <ChatView
              chatRoom={chatRoom}
              onSend={onSend}
              onFileDownload={onFileDownload}
              onFileUpload={onFileUpload}
              messages={activeMessages}
              onToggleMessagePin={onToggleMessagePin}
              isChatClosed={false}
              selectedMessageId={selectedMessageId}
              clearSelectedMessageId={() => setSelectedMessageId(null)}
              userId={props.userId}
              searchQuery={searchQuery}
              shouldShowAdditionalControls={!hideFeatures}
              activeParentTab={activeParentTab}
              agentReplyActions={agentReplyActions}
              isGeneralThread
              initialContent={initialMessage}
            />
          </div>
        ) : (
          <Tabs
            value={currentTab}
            onValueChange={setCurrentTab}
            className="flex flex-col h-full min-h-0"
          >
            <ChatRoomTabList
              isSearchActive={isSearchActive}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              activeTab={currentTab}
              onTabChange={setCurrentTab}
            />
            <div className="flex-1 min-h-0 overflow-hidden">
              <TabsContent value="chat" className="h-full min-h-0">
                <ChatView
                  chatRoom={chatRoom}
                  onSend={onSend}
                  onFileDownload={onFileDownload}
                  onFileUpload={onFileUpload}
                  messages={activeMessages}
                  onToggleMessagePin={onToggleMessagePin}
                  isChatClosed={isChatClosed}
                  selectedMessageId={selectedMessageId}
                  clearSelectedMessageId={() => setSelectedMessageId(null)}
                  userId={props.userId}
                  searchQuery={searchQuery}
                  shouldShowAdditionalControls={!hideFeatures}
                  activeParentTab={activeParentTab}
                  agentReplyActions={agentReplyActions}
                  initialContent={initialMessage}
                />
              </TabsContent>
              <TabsContent value="documents" className="h-full min-h-0">
                <DocumentsView
                  messages={activeMessages}
                  onFileDownload={onFileDownload}
                />
              </TabsContent>
              <TabsContent value="pinned" className="h-full min-h-0">
                <PinnedChatView
                  onFileDownload={onFileDownload}
                  messages={activeMessages}
                  onMessageClick={navigateToMessage}
                  onToggleMessagePin={onToggleMessagePin}
                  userId={props.userId}
                />
              </TabsContent>
            </div>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export { ChatRoom };
